import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { EditActionBar } from '../shared/EditActionBar';
import type { Driver09Data, FormazioneSection } from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { formatHighlights } from '../../../utils/Format';

const fmtN = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('it-IT');

/* ─── Helpers ────────────────────────────────────────────── */
const Num = ({ n }: { n: number | null | undefined }) => <>{fmtN(n)}</>;

const Cell = ({ n, amber = false }: { n: number | null | undefined; amber?: boolean }) => {
  if (amber) return (
    <span className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-amber-700 font-bold text-[11px]">
      {fmtN(n)}
    </span>
  );
  return <span className="font-semibold text-gray-700">{fmtN(n)}</span>;
};

const numCls = 'w-20 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';

/* ─── Main ───────────────────────────────────────────────── */
const FruizioneFormazioneContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver09Data>(reportId, 9);

  const [data,      setData]      = useState<Driver09Data | null>(null);
  const [savedData, setSavedData] = useState<Driver09Data | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (remoteData) {
      setData(remoteData);
      setSavedData(remoteData);
    }
  }, [remoteData]);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  const updateField = (
    section: 'non_completion' | 'late_completion',
    key: keyof FormazioneSection,
    raw: string,
  ) => {
    const val = parseInt(raw, 10);
    if (isNaN(val)) return;
    setData(prev => (prev ? { ...prev, [section]: { ...prev[section], [key]: val } } : prev));
  };

  const updateHighlights = (value: string) => {
    setData(prev => (prev ? { ...prev, highlights: value } : prev));
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  const nonCompletion  = data.non_completion;
  const lateCompletion = data.late_completion;

  return (
    <div className="p-6 space-y-5">

      <div className="flex items-center justify-end">
        <EditActionBar
          isEditing={isEditing}
          onProcedi={() => setIsEditing(true)}
          onAnnulla={handleAnnulla}
          onSalva={handleSalva}
          disabled={isReadOnly}
        />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Totale mancata fruizione',      val: nonCompletion.total,  cls: 'text-[#1B365D]' },
          { label: 'Totale ritardo nella fruizione', val: lateCompletion.total, cls: 'text-amber-600' },
        ].map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            className="bg-white rounded-xl border border-gray-100 p-5 text-center"
          >
            <div className={`text-3xl font-bold ${c.cls}`}>{fmtN(c.val)}</div>
            <div className="text-[11px] text-gray-400 mt-1">{c.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Tabella */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100">
          <p className="text-xs font-semibold text-[#1B365D]">
            Formazione obbligatoria — {data.period}
          </p>
        </div>

        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
              <th className="text-left  px-5 py-2.5 font-semibold">Formazione obbligatoria</th>
              <th className="text-right px-5 py-2.5 font-semibold">Mancata fruizione</th>
              <th className="text-right px-5 py-2.5 font-semibold text-amber-500">Ritardo nella fruizione</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50">
            <motion.tr
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.06 }}
              className="hover:bg-gray-50/60 transition-colors"
            >
              <td className="px-5 py-3.5 font-semibold text-[#1B365D]">Dipendenti</td>
              <td className="px-5 py-3.5 text-right">
                {isEditing
                  ? <input type="number" className={numCls} value={nonCompletion.employees} onChange={e => updateField('non_completion', 'employees', e.target.value)} />
                  : <Cell n={nonCompletion.employees} />}
              </td>
              <td className="px-5 py-3.5 text-right">
                {isEditing
                  ? <input type="number" className={numCls} value={lateCompletion.employees} onChange={e => updateField('late_completion', 'employees', e.target.value)} />
                  : <Cell n={lateCompletion.employees} amber />}
              </td>
            </motion.tr>

            <motion.tr
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="hover:bg-gray-50/60 transition-colors"
            >
              <td className="px-5 py-3.5 font-semibold text-[#1B365D]">Direttori di Area</td>
              <td className="px-5 py-3.5 text-right">
                {isEditing
                  ? <input type="number" className={numCls} value={nonCompletion.area_directors} onChange={e => updateField('non_completion', 'area_directors', e.target.value)} />
                  : <Cell n={nonCompletion.area_directors} />}
              </td>
              <td className="px-5 py-3.5 text-right">
                {isEditing
                  ? <input type="number" className={numCls} value={lateCompletion.area_directors} onChange={e => updateField('late_completion', 'area_directors', e.target.value)} />
                  : <Cell n={lateCompletion.area_directors} amber />}
              </td>
            </motion.tr>
          </tbody>

          <tfoot>
            <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
              <td className="px-5 py-3">Totale</td>
              <td className="px-5 py-3 text-right">
                {isEditing
                  ? <input type="number" className="w-20 text-right bg-white/10 border border-white/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-white/50 tabular-nums text-white" value={nonCompletion.total} onChange={e => updateField('non_completion', 'total', e.target.value)} />
                  : <Num n={nonCompletion.total} />}
              </td>
              <td className="px-5 py-3 text-right">
                {isEditing
                  ? <input type="number" className="w-20 text-right bg-white/10 border border-white/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-white/50 tabular-nums text-white" value={lateCompletion.total} onChange={e => updateField('late_completion', 'total', e.target.value)} />
                  : <Num n={lateCompletion.total} />}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Highlights */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed"
      >
        <span className="font-semibold">Highlights: </span>
        {isEditing ? (
          <textarea
            className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[100px]"
            value={data.highlights}
            onChange={e => updateHighlights(e.target.value)}
          />
        ) : (
          <span className="whitespace-pre-wrap">{formatHighlights(data.highlights)}</span>
        )}
      </motion.div>

    </div>
  );
};

export default FruizioneFormazioneContainer;
